import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import style from './last-updated-tag.module.less';
import { messages } from './messages';
import { MarketplaceIds } from '../../../../enums/marketplace-ids';
import { useEnable24HourShopifyProductSync } from '../../../../feature-flags/hooks/use-enable-24-hour-shopify-product-sync';
import { useSellerFormattedDateTime } from '../../../../hooks/use-datetime-formats';
import { BASE_SETTINGS_PATH, BASE_STORE_SETUP_URL } from '../../../../routes/constants';
import Global from '../../../../store/global-selectors';
import { Button } from '../../../../ui-components/components/button';
import { FlexRow, Gap } from '../../../../ui-components/components/flex';
import { Tag, TagType } from '../../../../ui-components/components/tag';
import { Text } from '../../../../ui-components/components/text';
import { TextType } from '../../../../ui-components/components/text/text-type';
import { THEME_CONSTANTS } from '../../../../ui-components/services/button-theme-service';
import { settingsTabs } from '../../../settings/stores/constants';
import { getProductsRefreshStatus } from '../../api/products-refresh-status.api';

import type { ProductRefreshStatus } from '../../api/products-refresh-status.api';

type Store = {
  marketplaceId: number;
  storeGuid: string;
  isActive: boolean;
};

const findMostRecentDate = (data: ProductRefreshStatus[]): DateTime | null => {
  if (data.length === 0) {
    return null;
  }

  return data.reduce(
    (latest, store) => {
      if (!store?.lastUpdated) {
        return latest;
      }
      const storeDate = DateTime.fromISO(store.lastUpdated, { zone: 'utc' });
      if (!latest || storeDate > latest) {
        return storeDate;
      }
      return latest;
    },
    null as DateTime | null,
  );
};

export const LastUpdatedTag = () => {
  const is24HourShopifyProductSyncEnabled = useEnable24HourShopifyProductSync();
  const navigate = useNavigate();
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const stores = useSelector(Global.selectSellerStores);

  const hasShopifyStore = useMemo(() => {
    return stores.some((store: Store) => store.marketplaceId === MarketplaceIds.Shopify);
  }, [stores]);

  const [refreshStatusData, setRefreshStatusData] = useState<ProductRefreshStatus[]>([]);

  useEffect(() => {
    if (!is24HourShopifyProductSyncEnabled || !sellerTimeZone || !hasShopifyStore) {
      return;
    }

    getProductsRefreshStatus()
      .then((data) => {
        setRefreshStatusData(data);
      })
      .catch(() => {
        setRefreshStatusData([]);
      });
  }, [sellerTimeZone, is24HourShopifyProductSyncEnabled, hasShopifyStore]);

  const lastUpdatedTimestamp = useMemo(() => {
    const mostRecentDate = findMostRecentDate(refreshStatusData);
    return mostRecentDate?.toISO() ?? null;
  }, [refreshStatusData]);

  const formattedLastUpdatedDateTime = useSellerFormattedDateTime(lastUpdatedTimestamp);

  const isSingleStore = refreshStatusData.length === 1;
  const singleStoreGuid = isSingleStore ? refreshStatusData[0].storeGuid : null;

  const handleEditSyncSettings = useCallback(() => {
    if (singleStoreGuid) {
      const storeSettingsPath = `${BASE_SETTINGS_PATH}/stores/${singleStoreGuid}/${settingsTabs.products}`;
      navigate(storeSettingsPath);
    } else {
      navigate(BASE_STORE_SETUP_URL);
    }
  }, [navigate, singleStoreGuid]);

  if (!is24HourShopifyProductSyncEnabled || !formattedLastUpdatedDateTime || !hasShopifyStore) {
    return null;
  }

  return (
    <Tag
      message={
        <FlexRow className={style['tag-content']} gap={Gap.Space8}>
          <Text
            textType={TextType.Caption}
            value={<FormattedMessage {...messages.lastUpdatedFromShopify} values={{ date: formattedLastUpdatedDateTime }} />}
          />
          <Button onClick={handleEditSyncSettings} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={messages.editSyncSettings} />
          </Button>
        </FlexRow>
      }
      showStatusDot={false}
      type={TagType.Neutral}
    />
  );
};
